import React from "react"

import { BodyCopyRegular, Box, Headline, SubHeadline } from "@theguarantors/ui-kit-v2/dist/design-system/typography"
import { backgrounds, colors } from "@theguarantors/ui-kit-v2/dist/design-system/colors"
import { Card, Section, GetStartedButton } from "../LandingPage.styles"
import { ResponsiveRow, Col } from "@theguarantors/ui-kit-v2/dist/layout/grid"

import { LicenseIcon, CheckmarkIcon, BriefcaseIcon, SpellcheckIcon } from "../../../components/icons"
import { FeatureFlag, useFeatureFlag } from "../../../hooks/use-feature-flag"

const letsGetStarted = () => window.scrollTo(0, 250)

export const HowItWorks: React.FC = () => {
  const [rebrandV2MonolithReact] = useFeatureFlag(FeatureFlag.REBRAND_V2_MONOLITH_REACT)
  const rebrandV2MonolithReactFeatureFlag: Boolean = rebrandV2MonolithReact === "on"

  const [newGetStartedPage] = useFeatureFlag(FeatureFlag.NEW_GET_STARTED_PAGE)
  const newGetStartedPageFeatureFlag: boolean = newGetStartedPage === "on"

  if (newGetStartedPageFeatureFlag) {
    return (
      <Section>
        <Box display="flex" justifyContent="center" mt="64px">
          <Headline
            color={colors.v2_black}
            display="inline-block"
            background={rebrandV2MonolithReactFeatureFlag ? "" : backgrounds.aquamarine_underline}
            fontFamily="ivar"
            fontWeight="400"
            margin="0"
            size="xl"
          >
            How it works:
          </Headline>
        </Box>
        <ResponsiveRow hgutter="30px">
          <Col>
            <Card color={colors.v2_black} gap="0.6rem">
              <LicenseIcon />
              <SubHeadline size="xl" color={colors.v2_black} fontFamily="acid" fontWeight="400">
                Apply
              </SubHeadline>
              <ul>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  Apply when you’ve found an apartment that works with us.
                </BodyCopyRegular>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  Start by filling out the form above.
                </BodyCopyRegular>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  Submit your application and provide all required information.
                </BodyCopyRegular>
              </ul>
            </Card>
          </Col>
          <Col>
            <Card color={colors.v2_black} gap="0.6rem">
              <BriefcaseIcon />
              <SubHeadline size="xl" color={colors.v2_black} fontFamily="acid" fontWeight="400">
                {newGetStartedPageFeatureFlag ? "Get qualified" : "Get Qualified"}
              </SubHeadline>
              <ul>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  We’ll get you a same day quote.
                </BodyCopyRegular>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  Sign your coverage agreement.
                </BodyCopyRegular>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  Pay your policy premium.
                </BodyCopyRegular>
              </ul>
            </Card>
          </Col>
          <Col>
            <Card color={colors.v2_black} gap="0.6rem">
              <SpellcheckIcon />
              <SubHeadline size="xl" color={colors.v2_black} fontFamily="acid" fontWeight="400">
                Sign your lease
              </SubHeadline>
              <ul>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  We’ll inform your landlord of your coverage.
                </BodyCopyRegular>
                <BodyCopyRegular as="li" fontFamily="acid" display="flex">
                  <span>
                    <CheckmarkIcon />
                  </span>{" "}
                  You’re ready to sign your lease and move in.
                </BodyCopyRegular>
              </ul>
            </Card>
          </Col>
        </ResponsiveRow>

        <Box pl="3.2rem" pr="3.2rem" mt="10px" alignSelf="center" textAlign="center">
          <GetStartedButton
            variant={rebrandV2MonolithReactFeatureFlag ? "v2_primary" : "primary"}
            onClick={letsGetStarted}
            newGetStartedPageFeatureFlag={newGetStartedPageFeatureFlag}
            fontFamily="acid"
          >
            Get Started
          </GetStartedButton>
        </Box>
      </Section>
    )
  }

  return (
    <Section>
      <Box display="flex" justifyContent="center" mt="6rem">
        <Headline
          color="violet"
          display="inline-block"
          background={rebrandV2MonolithReactFeatureFlag ? "" : backgrounds.aquamarine_underline}
        >
          How it works:
        </Headline>
      </Box>
      <ResponsiveRow>
        <Col>
          <Card>
            <LicenseIcon />
            <Headline size="m" color="violet">
              Apply
            </Headline>
            <SubHeadline color="violet" size="m">
              Begin by clicking “Let’s Get Started”
            </SubHeadline>
            <ul>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> Fill out questionnaire above
              </BodyCopyRegular>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> Complete all required information and provide documents
              </BodyCopyRegular>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> We'll get you a same day quote
              </BodyCopyRegular>
            </ul>
          </Card>
        </Col>
        <Col>
          <Card>
            <BriefcaseIcon />
            <Headline size="m" color="violet">
              Get Qualified
            </Headline>
            <SubHeadline color="violet" size="m">
              Our team of live experts will work directly with you to:
            </SubHeadline>
            <ul>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> Finalize all information provided
              </BodyCopyRegular>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> Complete the lease guarantee premium payment
              </BodyCopyRegular>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> Get qualified for your apartment
              </BodyCopyRegular>
            </ul>
          </Card>
        </Col>
        <Col>
          <Card>
            <SpellcheckIcon />
            <Headline size="m" color="violet">
              Sign Your Lease
            </Headline>
            <SubHeadline color="violet" size="m">
              That’s it! You’re done!
            </SubHeadline>
            <ul>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> We'll let your landlord know you've secured a lease guarantee
              </BodyCopyRegular>
              <BodyCopyRegular as="li">
                <CheckmarkIcon /> You're ready to sign your lease!
              </BodyCopyRegular>
            </ul>
          </Card>
        </Col>
      </ResponsiveRow>

      <Box pl="3.2rem" pr="3.2rem" mt={["5.4rem", "9rem"]} mr={[0, 0, "5rem"]} alignSelf="center" textAlign="center">
        <GetStartedButton
          variant={rebrandV2MonolithReactFeatureFlag ? "v2_primary" : "primary"}
          onClick={letsGetStarted}
        >
          Let's Get Started
        </GetStartedButton>
      </Box>
    </Section>
  )
}
